/**
 * Page de connexion à l'ERP — Lot B.1.7.
 *
 * Flow :
 *   1. credentials → email + mot de passe (validés par `loginInput` du contracts)
 *      ├─ success → cookie posé, identité fraîche puis choix/handoff du domaine
 *      └─ mfa_required → bascule vers le step "mfa" (sans rechargement)
 *   2. mfa → code TOTP 6 chiffres (ou code de récupération en alternative)
 *      └─ success → même finalisation, sans rejouer le challenge MFA
 *
 * Validation : react-hook-form + zod via le schéma partagé `loginInput`
 * (`@champ-erp/contracts`). Une seule source de vérité client + serveur.
 *
 * Anti-énumération : pour `UNAUTHORIZED` ou `BAD_REQUEST`, on affiche un
 * message générique unique ("Email ou mot de passe incorrect") afin de ne
 * pas fuiter quel champ est en cause.
 *
 * Le challenge MFA est délégué au composant partagé `<MfaCodePrompt />`
 * (réutilisé par MagicLinkPage). Cette page ne s'occupe que du step
 * credentials et du routing post-authentification.
 */

import { extractTrpcCode, NETWORK_ERROR_MESSAGE } from "@champ-erp/auth-primitives/web";
// Import par SOUS-CHEMIN (pas le barrel `@champ-erp/contracts`) : /login est sur
// le chemin critique et le barrel réexporte 61 modules métier.
import { loginInput, type LoginInput } from "@champ-erp/contracts/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { MUTATION_SANS_RECUPERATION_SESSION_META } from "../auth/mutation-session-recovery.js";
import { ME_QUERY_KEY } from "../auth/use-me.js";
import { MfaCodePrompt } from "../components/auth/MfaCodePrompt.js";
import { PasswordInput } from "../components/PasswordInput.js";
import { AuthLayout } from "../layouts/AuthLayout.js";
import { inputClasses } from "../lib/auth-form-classes.js";
import { redirectToTenantViaHandoff } from "../lib/handoff-redirect.js";
import { appendNextIfMeaningful, safeNextFromSearch } from "../lib/safe-next.js";
import { trpc } from "../lib/trpc.js";

type Step = "credentials" | "mfa";

export function LoginPage() {
  const navigate = useNavigate();
  // `?next=` mémorise la page que l'user voulait atteindre avant le redirect
  // /login (posée par RequireAuth). On la propage à /choisir-domaine pour
  // les multi-tenant ; pour les mono-tenant on perd l'info après handoff
  // (cf note dans `routeAfterAuthSuccess`). Validée anti open-redirect.
  const nextTarget = safeNextFromSearch(window.location.search);
  // RequireAuth doit trouver une identité fraîche en cache avant la navigation.
  // refetchQueries ne suffit pas : cache froid ignoré et erreurs absorbées.
  const queryClient = useQueryClient();

  /**
   * Routage post-authentification (login mono-step ou post-MFA).
   *
   * Cas multi-tenant sans MFA (`mfa_setup_required: true`, Lot B.2.10) :
   * la session a été posée, mais le user doit activer MFA AVANT d'accéder
   * à l'ERP. On le redirige vers /profile/mfa/setup. Côté backend, le
   * middleware `mfaSetupGuardMiddleware` bloque les routes métier tant
   * que MFA n'est pas activé.
   *
   * Sinon, trois cas selon `domaine_id_actif` :
   *   - non null (mono-tenant OU tenant déjà choisi) → handoff direct vers
   *     `<slug>.crayea.fr/auth/handoff?token=XXX`. La page actuelle décharge,
   *     l'user atterrit sur le sous-domaine avec le cookie ERP posé.
   *   - null (multi-tenant ou aucune membership) → /choisir-domaine, qui
   *     gère la suite : 0 membership → /aucun-domaine, N → liste de choix.
   *
   * Le `?next=` est propagé à /choisir-domaine. Pour le cas mono-tenant,
   * le `?next=` est perdu après handoff (pas de mécanisme cross-host
   * implémenté pour V0) — l'user atterrit sur `/` du sous-domaine.
   */
  const routeAfterAuthSuccess = async (
    domaineIdActif: string | null,
    mfaSetupRequired: boolean,
    emailVerificationRequired: boolean,
  ) => {
    // B.4.18 — Confirmation d'email obligatoire posée AVANT toute autre garde :
    // tant que `email_verifie_le IS NULL`, le backend (emailVerifiedGuardMiddleware)
    // rejette `auth.createHandoff` et toutes les routes tenant. Tenter un handoff
    // direct ici ferait fallback sur /choisir-domaine + message d'erreur
    // trompeur. On force l'user sur la page d'attente où il pourra cliquer
    // le mail / renvoyer un nouveau lien.
    if (emailVerificationRequired) {
      navigate("/confirmer-email-attente", { replace: true });
      return;
    }
    // L'invitation s'accepte sur le hub avec la session issue du login/MFA.
    // Un handoff préalable perdrait cette destination pour un mono-tenant.
    if (nextTarget.startsWith("/invitation/")) {
      navigate(nextTarget, { replace: true });
      return;
    }
    if (mfaSetupRequired) {
      navigate("/profile/mfa/setup?reason=multi_tenant", { replace: true });
      return;
    }
    if (domaineIdActif) {
      try {
        await redirectToTenantViaHandoff(domaineIdActif);
        return;
      } catch {
        // Cas rare : membership révoquée entre login et handoff, ou domaine
        // archivé. On délègue à /choisir-domaine qui réagira au state actuel
        // via auth.me (et redirigera vers /aucun-domaine si plus aucune
        // membership active).
      }
    }
    navigate(appendNextIfMeaningful("/choisir-domaine", nextTarget), { replace: true });
  };

  const [step, setStep] = useState<Step>("credentials");
  const [preMfaToken, setPreMfaToken] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [finalisation, setFinalisation] = useState<"idle" | "loading" | "error">("idle");
  const finalisationEnCours = useRef(false);

  // ──────────────────────────────────────────────────────────────────────────
  // Form 1 : credentials (email + mot de passe)
  // ──────────────────────────────────────────────────────────────────────────

  const credentialsForm = useForm<LoginInput>({
    resolver: zodResolver(loginInput),
    defaultValues: { email: "", mot_de_passe: "" },
    mode: "onSubmit",
  });

  const finaliserConnexion = async () => {
    if (finalisationEnCours.current) return;
    finalisationEnCours.current = true;
    setFinalisation("loading");
    setPreMfaToken(null);
    credentialsForm.reset({ email: credentialsForm.getValues("email"), mot_de_passe: "" });
    try {
      // Ne pas réutiliser une lecture démarrée avant la création de la session.
      await queryClient.cancelQueries({ queryKey: ME_QUERY_KEY, exact: true });
      const me = await queryClient.fetchQuery({
        queryKey: ME_QUERY_KEY,
        queryFn: () => trpc.auth.me.query(),
        staleTime: 0,
        retry: false,
      });
      await routeAfterAuthSuccess(
        me.domaine_id_actif,
        me.mfa_setup_required,
        me.email_verification_required,
      );
    } catch {
      // Le serveur a déjà validé l'authentification : une panne d'identité ne
      // doit ni devenir un refus d'identifiants ni rejouer un jeton MFA consommé.
      setFinalisation("error");
    } finally {
      finalisationEnCours.current = false;
    }
  };

  const loginMutation = useMutation({
    meta: MUTATION_SANS_RECUPERATION_SESSION_META,
    mutationFn: (input: LoginInput) => trpc.auth.login.mutate(input),
    onSuccess: async (result) => {
      setGlobalError(null);
      if (result.type === "mfa_required") {
        // Bascule vers le step MFA sans rechargement, on garde le pre_mfa_token
        // (TTL 5min côté backend) en RAM.
        setPreMfaToken(result.pre_mfa_token);
        setStep("mfa");
        // Sécu : on ne garde pas le password en mémoire JS plus longtemps
        // que strictement nécessaire.
        credentialsForm.reset({ email: credentialsForm.getValues("email"), mot_de_passe: "" });
        return;
      }
      await finaliserConnexion();
    },
    onError: (error) => {
      const code = extractTrpcCode(error);
      // Note : le bandeau « MFA obligatoire si multi-tenant » n'est plus géré
      // ici. Le backend pose désormais la session au login et retourne
      // `mfa_setup_required: true` (vu en `onSuccess`) → `routeAfterAuthSuccess`
      // bascule sur `/profile/mfa/setup` côté hub. Pas d'erreur FORBIDDEN à
      // intercepter pour ce cas.
      // Anti-énumération + UX : tout échec d'identification (mauvais mdp, email
      // inconnu, compte verrouillé, format invalide) → même message générique.
      if (code === "UNAUTHORIZED" || code === "BAD_REQUEST") {
        setGlobalError("Email ou mot de passe incorrect.");
      } else if (code === "TOO_MANY_REQUESTS") {
        setGlobalError("Trop de tentatives. Réessayez dans 15 minutes.");
      } else {
        setGlobalError(NETWORK_ERROR_MESSAGE);
      }
    },
  });

  const onCredentialsSubmit = (values: LoginInput) => {
    setGlobalError(null);
    loginMutation.mutate(values);
  };

  const handleBackToCredentials = () => {
    setStep("credentials");
    setPreMfaToken(null);
    setGlobalError(null);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Rendu — sélection du formulaire selon le step
  // ──────────────────────────────────────────────────────────────────────────

  if (finalisation !== "idle") {
    return (
      <AuthLayout title="Connexion confirmée" subtitle="Préparation de votre accès à l’ERP.">
        {finalisation === "error" ? (
          <>
            <p role="alert" className="mb-4 text-sm text-red-700">
              Votre authentification a réussi, mais votre accès n’a pas pu être chargé. Réessayez
              sans ressaisir vos identifiants ni votre code de sécurité.
            </p>
            <button
              type="button"
              onClick={() => void finaliserConnexion()}
              className="min-h-[44px] w-full rounded-lg bg-champagne-800 px-4 py-3 font-medium text-white hover:bg-champagne-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-champagne-700"
            >
              Réessayer l’accès
            </button>
          </>
        ) : (
          <p role="status" aria-live="polite" className="text-sm text-champagne-800">
            Chargement de votre accès…
          </p>
        )}
      </AuthLayout>
    );
  }

  if (step === "mfa" && preMfaToken) {
    return (
      <MfaCodePrompt
        preMfaToken={preMfaToken}
        onBack={handleBackToCredentials}
        onSuccess={finaliserConnexion}
      />
    );
  }

  // Step credentials
  const emailError = credentialsForm.formState.errors.email?.message;
  const passwordError = credentialsForm.formState.errors.mot_de_passe?.message;
  // Bandeau « session récupérée » : affiché quand l'utilisateur a été
  // basculé ici par le handler global de main.tsx suite à un cookie session
  // orphelin / désynchronisé. Évite que l'écran ait l'air d'apparaître
  // « tout seul » sans explication.
  const wasRecovered = new URLSearchParams(window.location.search).get("recovered") === "session";

  return (
    <AuthLayout
      title="Connexion à votre espace"
      subtitle="Accédez à votre tableau de bord ERP."
      footer={
        <p className="text-champagne-800/80">
          Pas encore de compte ?{" "}
          <a
            href="/signup"
            className="font-medium text-champagne-700 underline-offset-2 hover:text-champagne-900 hover:underline"
          >
            Démarrer l&apos;essai gratuit
          </a>
        </p>
      }
    >
      {wasRecovered && (
        <p
          role="status"
          aria-live="polite"
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900"
        >
          Votre session précédente a expiré. Reconnectez-vous pour continuer.
        </p>
      )}
      <form
        onSubmit={credentialsForm.handleSubmit(onCredentialsSubmit)}
        className="space-y-5"
        noValidate
      >
        <div>
          <label
            htmlFor="login-email"
            className="mb-1.5 block text-sm font-medium text-champagne-800"
          >
            Adresse email
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="username"
            autoFocus
            placeholder="nom@exemple.fr"
            aria-invalid={emailError ? "true" : "false"}
            {...credentialsForm.register("email")}
            className={inputClasses(!!emailError)}
          />
          {emailError && (
            <p role="alert" className="mt-1.5 text-xs font-medium text-red-700">
              {emailError}
            </p>
          )}
        </div>

        {/* Lien "Oublié ?" placé APRÈS l'input dans le DOM (tab order : email →
            password → bouton) mais positionné visuellement à droite du label. */}
        <div className="relative">
          <label
            htmlFor="login-password"
            className="mb-1.5 block text-sm font-medium text-champagne-800"
          >
            Mot de passe
          </label>
          <PasswordInput
            id="login-password"
            autoComplete="current-password"
            hasError={!!passwordError}
            {...credentialsForm.register("mot_de_passe")}
          />
          {passwordError && (
            <p role="alert" className="mt-1.5 text-xs font-medium text-red-700">
              {passwordError}
            </p>
          )}
          <a
            href="/reset-password"
            className="absolute right-0 top-0 text-xs text-champagne-700 underline-offset-2 hover:text-champagne-900 hover:underline"
          >
            Oublié ?
          </a>
        </div>

        {globalError && (
          <p
            id="login-error"
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800"
          >
            {globalError}
          </p>
        )}

        <button
          type="submit"
          disabled={loginMutation.isPending}
          className="cta-flat inline-flex h-12 w-full items-center justify-center rounded-xl text-[15px] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loginMutation.isPending ? "Connexion…" : "Se connecter"}
        </button>

        <div className="flex items-center gap-3 text-xs text-champagne-800/50">
          <span className="h-px flex-1 bg-champagne-800/15" />
          <span className="uppercase tracking-[0.18em]">ou</span>
          <span className="h-px flex-1 bg-champagne-800/15" />
        </div>

        <a
          href="/magic-link"
          className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-champagne-800/25 bg-white text-[15px] font-semibold text-champagne-900 transition hover:border-champagne-500 hover:text-champagne-700"
        >
          Recevoir un lien de connexion par email
        </a>
      </form>
    </AuthLayout>
  );
}
